<template>
  <div
    class="ai-dialog-container d-flex flex-column"
    :class="{
      closed: !isOpen,
      'margin-with-trigger': floatingTriggerExist,
    }"
  >
    <div
      class="ai-dialog-header pa-5 d-flex justify-space-between align-center"
    >
      <div class="d-flex align-center justify-center">
        <v-btn
          v-if="activeAction && showMultipleActions"
          class="ma-0 pa-0 me-4"
          icon
          x-small
          :ripple="false"
          @click="handleBack"
        >
          <v-icon
            class="cursor-pointer ma-0 pa-0"
            color="blue lighten-1"
            size="28"
          >
            mdi-chevron-left
          </v-icon>
        </v-btn>

        <v-icon color="blue lighten-1" size="24" class="pa-0 ma-0"
          >mdi-creation-outline</v-icon
        >
        <span class="ml-2 h6">{{ $t('ai_features.rever_ai') }}</span>
      </div>

      <v-btn
        icon
        x-small
        :ripple="false"
        @click="handleDialogClose"
        class="ma-0 pa-0"
      >
        <v-icon
          color="blue lighten-1"
          size="20"
          class="cursor-pointer ma-0 pa-0"
          >mdi-close</v-icon
        >
      </v-btn>
    </div>

    <v-divider class="ai-dialog-divider" />

    <AiErrorAlert
      v-if="!hasTokensAvailable"
      :errorType="aiErrorStatusCode"
      :textClass="['body-2']"
      :alertClass="['my-6', 'mx-4']"
    />

    <AiActionWrapper v-else-if="!activeAction">
      <template v-slot:content>
        <div>
          <p class="mb-4 body-2 blue-grey--text text--lighten-2">
            {{ $t('ai_features.ai_assistant_message') }}
          </p>

          <div
            v-if="isRevCompleted"
            class="ai-actionable button mb-3"
            @click="activeAction = 'summary'"
          >
            {{ $t('ai_features.create_rev_summary') }}
          </div>

          <div
            v-if="shouldShowTaskSuggestion"
            class="ai-actionable button  mb-3"
            @click="activeAction = 'taskSuggestion'"
          >
            {{ $t('ai_features.suggest_tasks_for_this_rev') }}
          </div>

          <div
            v-if="shouldShowSolutionSuggestion"
            class="ai-actionable button"
            @click="activeAction = 'solutionSuggestion'"
          >
            {{ $t('ai_features.suggest_a_solution') }}
          </div>
        </div>
      </template>

      <template v-slot:footer>
        <span
          v-html="$t('ai_features.disclaimer_short')"
          class="caption blue-grey--text py-2"
        />
      </template>
    </AiActionWrapper>

    <AiSummary v-else-if="activeAction === 'summary'" :rev="rev" />

    <AiTasksSuggestion
      v-else-if="activeAction === 'taskSuggestion'"
      :rev="rev"
      :isOpen="isOpen"
      :skipDescription="suggestTasks"
      @handleDialogClose="handleDialogClose"
    />

    <AiSolution v-else-if="activeAction === 'solutionSuggestion'" :rev="rev" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

import AiErrorAlert from '@/components/AIManagement/AiErrorAlert.vue'
import AiActionWrapper from './AiActionWrapper.vue'
import AiSummary from './AiSummary.vue'
import AiTasksSuggestion from './AiTasksSuggestion.vue'
import AiSolution from './AiSolution.vue'

export default {
  name: 'AiAssistantDialog',
  components: {
    AiActionWrapper,
    AiSummary,
    AiTasksSuggestion,
    AiSolution,
    AiErrorAlert,
  },

  data() {
    return {
      isOpen: false,
      rev: {},
      floatingTriggerExist: false,
      suggestTasks: false,
      activeAction: null,
      isLoading: true,
      hasTokensAvailable: false,
      aiErrorStatusCode: 0,
      showMultipleActions: false,
    }
  },

  watch: {
    '$route.path'() {
      this.handleDialogClose()
    },
  },

  async mounted() {
    await this.checkTokensAvailability()
    eventHub.$on(HUB_EVENTS.AI.HUB_DIALOG_OPEN, this.handleDialogOpen)
  },

  computed: {
    ...mapGetters(['organizationId']),

    isRevCompleted() {
      return (
        (this.rev?.status ?? this.rev?.revCondition) ===
        this.$constants.revStatus.completed
      )
    },

    hasSolution() {
      return Boolean(
        this.rev?.afterPhase?.solution ??
          this.rev?.solutionDescription ??
          this.rev?.expand?.solution
      )
    },

    isAiTasksSuggestionEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys.aiTasksSuggestion)
    },

    isAiSolutionSuggestionEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys.aiSolutionSuggestion)
    },

    shouldShowTaskSuggestion() {
      return this.isAiTasksSuggestionEnabled && this.hasSolution
    },

    shouldShowSolutionSuggestion() {
      return (
        this.isAiSolutionSuggestionEnabled &&
        !this.hasSolution &&
        !this.isRevCompleted
      )
    },
  },

  methods: {
    async checkTokensAvailability() {
      try {
        const response = await api.ai.getTokensAvailability(this.organizationId)
        if (response.data) this.hasTokensAvailable = true
        return true
      } catch (err) {
        this.aiErrorStatusCode = err.response?.status
        this.hasTokensAvailable = false
        return false
      }
    },

    async handleDialogOpen({
      rev,
      hasFloatingTrigger = false,
      showMultipleActions = false,
      createSummary = false,
      suggestTasks = false,
    }) {
      this.floatingTriggerExist = hasFloatingTrigger
      this.createSummary = createSummary
      this.suggestTasks = suggestTasks
      this.rev = rev
      this.showMultipleActions = showMultipleActions

      if (!showMultipleActions) {
        this.activeAction = this.suggestTasks ? 'taskSuggestion' : 'summary'
      }

      await this.$nextTick()
      this.isOpen = true
    },

    handleBack() {
      this.activeAction = ''
    },

    handleDialogClose() {
      this.isOpen = false
      this.showMultipleActions = false

      setTimeout(() => {
        this.activeAction = ''
      }, 500)
    },
  },

  beforeDestroy() {
    eventHub.$off(HUB_EVENTS.AI.HUB_DIALOG_OPEN)
  },
}
</script>

<style scoped lang="scss">
.closed {
  max-height: 0 !important;
  min-height: 0 !important;
  border: none !important;
  transition-duration: 0s !important;
}

.margin-with-trigger {
  bottom: 65px !important;
}
</style>

<style lang="scss">
.ai-dialog-container {
  color: $color-white;
  width: 382px;
  position: fixed;
  bottom: 20px;
  top: auto !important;
  right: 98px;
  z-index: 5;
  max-height: 85vh;
  background: linear-gradient($color-blue-darken2 0 0) padding-box,
    // This is the filling color.
      conic-gradient(
        from 180deg at 50% 50%,
        #b3d3ff 0deg,
        #3882f0 152.05deg,
        #b3d3ff 360deg
      )
      border-box;
  border: 2px solid transparent;
  border-radius: 8px;
  transition: max-height 0.3s ease-in-out;
  overflow: hidden;
}

.ai-dialog-header {
  height: 64px;
}

.ai-dialog-content {
  max-height: 50vh;
  overflow-y: auto;
  padding-left: 20px;
  padding-right: 20px;
  flex-grow: 1;
  padding-top: 16px;
  padding-bottom: 16px;
}

.ai-dialog-footer {
  display: flex;
  min-height: 58px;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  padding-left: 16px;
  padding-right: 16px;
  position: static;
  bottom: 0;
}

.ai-dialog-divider {
  border-color: $color-blue !important;
}

.ai-actionable {
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  cursor: pointer;
  background: linear-gradient($color-blue-darken2 0 0) padding-box,
    // This is the filling color.
      conic-gradient(
        from 180deg at 50% 50%,
        #b3d3ff 0deg,
        #3882f0 152.05deg,
        #b3d3ff 360deg
      )
      border-box;
  padding-top: 9px;
  padding-bottom: 9px;
  border: 2px solid transparent;
}

.ai-actionable.badge {
  width: 44px;
  height: 20px;
  border-radius: 4px;

  &--absolute {
    position: absolute;
    top: 8px;
    right: 8px;
  }
}

.ai-actionable.button {
  height: 32px;
  max-width: fit-content;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: 17px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);

  &--fixed {
    position: fixed;
    z-index: 1;
    bottom: 30px;
    right: 98px;
  }

  span {
    margin-left: 8px !important;
    font-size: $font-size-14 !important;
    font-weight: 500;
    line-height: 16px;
  }
}
</style>
